<template>
  <div id="modal" class="modal fade ncr2-modal" tabindex="-1" role="dialog" aria-labelledby="ncr2-modal-title"
    aria-hidden="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <header class="modal-header ncr2-modal-header">
          <h5 id="ncr2-modal-title" class="modal-title">{{ modalTitle }}</h5>
          <button type="button" class="close ncr2-modal-close" aria-label="Close" @click="close">
            <span aria-hidden="true">&times;</span>
          </button>
        </header>

        <form class="ncr2-modal-form" @submit.prevent="save">
          <div class="modal-body">
            <div v-if="errorMessage" class="ncr-pane-alert is-error">{{ errorMessage }}</div>

            <section class="ncr2-details-grid">
              <div class="ncr2-field ncr2-field--part">
                <SearchDropdown v-model="form.newpart" :items="parts" value-path="part" subtitle-path="description"
                  :get-search-texts="partSearchHaystacks" label="Part Number" required output-mode="valueWithSubtitle"
                  variant="form" :min-chars="2" :loading="partsLoading" :disabled="isReadOnly || isSaving"
                  aria-label="Part number" />
              </div>
              <label class="ncr2-check-field">
                <input v-model="form.process" type="checkbox" :disabled="isReadOnly || isSaving" />
                <span>Process issue</span>
              </label>

              <label class="ncr2-field ncr2-field--quantity">
                <span>Quantity</span>
                <Input v-model="form.quantity" type="number" variant="form" :min="1" :max="9999" :default-value="1"
                  numeric-only :disabled="isReadOnly || isSaving" />
              </label>
              <label class="ncr2-field ncr2-field--wide">
                <span>Serial No. / Identifiers</span>
                <Input v-model="form.identifier" variant="form" :disabled="isReadOnly || isSaving" />
              </label>
              <label class="ncr2-field ncr2-field--severity">
                <span>*Severity</span>
                <Select v-model="form.severity" :options="severityOptions" placeholder="-- Please Select --"
                  variant="form" aria-label="Severity" :disabled="isReadOnly || isSaving" />
              </label>

              <label class="ncr2-field ncr2-field--full">
                <span>*Description of problem</span>
                <TextArea v-model="form.description" variant="form" :disabled="isReadOnly || isSaving" />
              </label>

              <label class="ncr2-field ncr2-field--third">
                <span>*Stage</span>
                <Select v-model="form.stage" :options="stageOptions" placeholder="-- Please Select --" variant="form"
                  aria-label="Stage" :disabled="isReadOnly || isSaving" />
              </label>
              <label class="ncr2-field ncr2-field--third">
                <span>WO / PO Reference</span>
                <Input v-model="form.ref" variant="form" :disabled="isReadOnly || isSaving" />
              </label>
              <label class="ncr2-field ncr2-field--third">
                <span>*Time Lost (min)</span>
                <Input v-model="form.timelost" type="number" variant="form" :disabled="isReadOnly || isSaving" />
              </label>
            </section>

            <section v-if="mode === 'create'" class="ncr2-create-files">
              <label class="ncr2-create-file-button" for="ncr2-create-files">
                <i class="material-icons">attach_file</i> Attachment
              </label>
              <input id="ncr2-create-files" type="file" multiple :disabled="isSaving" @change="stageFiles" />
              <div v-if="stagedFiles.length" class="ncr2-staged-files">
                <span v-for="file in stagedFiles" :key="`${file.name}-${file.size}`">{{ file.name }}</span>
              </div>
            </section>

            <section v-else class="ncr2-tabs">
              <nav class="ncr2-tab-list" aria-label="NCR details">
                <button v-for="tab in tabs" :key="tab.key" type="button" :class="{ active: activeTab === tab.key }"
                  @click="activeTab = tab.key">
                  {{ tab.label }}
                  <span v-if="tab.key === 'files' && fileCount" class="ncr2-tab-count">{{ fileCount }}</span>
                </button>
              </nav>

              <div class="ncr2-tab-panel">
                <NcrMessagesPane v-if="activeTab === 'messages'" :record-id="recordId" :readonly="isReadOnly" />

                <div v-else-if="activeTab === 'capa'" class="ncr2-capa-grid">
                  <label class="ncr2-field ncr2-field--full">
                    <span>Corrective / Preventive Action</span>
                    <TextArea v-model="form.capa" variant="form" :disabled="isReadOnly || isSaving" />
                  </label>
                  <label class="ncr2-field">
                    <span>Issue</span>
                    <Select v-model="form.issue" :options="issueOptions" placeholder="Link issue" variant="form"
                      aria-label="Linked issue" :disabled="isReadOnly || isSaving" />
                  </label>
                  <label class="ncr2-field">
                    <span>Asana ID</span>
                    <Input v-model="form.asana" variant="form" :disabled="isReadOnly || isSaving" />
                    <a v-if="form.asana" :href="asanaUrl" target="_blank" rel="noopener">Open in Asana</a>
                  </label>
                  <label class="ncr2-field">
                    <span>Status</span>
                    <Select v-model="form.status" :options="statusOptions" variant="form" aria-label="Status"
                      :disabled="isReadOnly || isSaving" />
                  </label>
                  <label class="ncr2-field">
                    <span>Disposition</span>
                    <Select v-model="form.disposition" :options="dispositionOptions" placeholder="-- Please Select --"
                      variant="form" aria-label="Disposition" :disabled="isReadOnly || isSaving" />
                  </label>
                </div>

                <NcrFilesPane v-else-if="activeTab === 'files'" :record-id="recordId"
                  :legacy-files="record?.files || []" :readonly="isReadOnly" @remove-legacy="removeLegacyFile" />
                <NcrActivityPane v-else :record-id="recordId" />
              </div>
            </section>
          </div>

          <footer class="modal-footer ncr2-modal-footer">
            <label class="ncr2-shelf-toggle">
              <input v-model="form.ncr_shelf" type="checkbox" :disabled="isReadOnly || isSaving" />
              <span>Item(s) on quarantine shelf</span>
            </label>
            <div class="ncr2-modal-actions">
              <button v-if="mode !== 'create'" type="button" class="button pill -text" :disabled="isPrinting"
                @click="printLabel">
                <i class="material-icons">receipt_long</i>
                {{ isPrinting ? 'Queuing…' : 'Print' }}
              </button>
              <button v-if="!isReadOnly" type="submit" class="button pill -blue" :disabled="isSaving">
                {{ isSaving ? 'Saving…' : 'Save' }}
              </button>
              <button type="button" class="button pill -text" @click="close">Cancel</button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Unsubscribe } from 'firebase/firestore';

import { Input, SearchDropdown, Select, TextArea } from '@/components/ui';
import type { SelectOption } from '@/components/ui/select/Select';
import { uploadRecordFiles, validateLocalRecordFile } from '@/modules/common/files/RecordFileWorkflow';
import { fileClient } from '@/modules/common/files/FileClient';
import {
  createEmptyNcrForm,
  formFromRecord,
  ncrClient,
  type LegacyNcrFile,
  type NcrFormState,
  type NcrIssue,
  type NcrPart,
  type NcrRecord
} from '@/modules/ncr/NcrClient';
import { onDocumentModalEvent } from '@/components/modal-events';

import NcrActivityPane from './NcrActivityPane.vue';
import NcrFilesPane from './NcrFilesPane.vue';
import NcrMessagesPane from './NcrMessagesPane.vue';
import { registerNcrModalController, type NcrModalController } from './ncr-modal-controller';

type ModalMode = 'create' | 'edit' | 'read-only';
type TabKey = 'messages' | 'capa' | 'files' | 'activity';

const mode = ref<ModalMode>('create');
const record = ref<NcrRecord | null>(null);
const recordId = ref('');
const form = ref<NcrFormState>(createEmptyNcrForm());
const parts = ref<NcrPart[]>([]);
const partsLoading = ref(true);
const issues = ref<NcrIssue[]>([]);
const stagedFiles = ref<File[]>([]);
const activeTab = ref<TabKey>('messages');
const isSaving = ref(false);
const isPrinting = ref(false);
const errorMessage = ref('');
let unsubscribeRecord: Unsubscribe | null = null;
let disposeHiddenListener: (() => void) | null = null;
let openGeneration = 0;

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: 'messages', label: 'Messages' },
  { key: 'capa', label: 'CAPA' },
  { key: 'files', label: 'Files' },
  { key: 'activity', label: 'Activity' }
];

const severityOptions: SelectOption[] = [
  { value: 'S1', label: 'S1 - Critical' },
  { value: 'S2', label: 'S2 - Major' },
  { value: 'S3', label: 'S3 - Minor' },
  { value: 'S4', label: 'S4 - Low' }
];
const stageOptions: SelectOption[] = [
  { value: '1', label: 'Incoming QC' },
  { value: '2', label: 'In Process' },
  { value: '3', label: 'Post Build' }
];
const statusOptions: SelectOption[] = [
  { value: 'Pending Review', label: 'Pending Review' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Escalated', label: 'Escalated' },
  { value: 'Vendor Return', label: 'Returned to Vendor' },
  { value: 'Monitoring', label: 'Closed - Monitoring' },
  { value: 'Closed', label: 'Closed' }
];
const dispositionOptions: SelectOption[] = [
  { value: '4', label: 'Use-as-is' },
  { value: '3', label: 'Rework' },
  { value: '5', label: 'Vendor Return' },
  { value: '2', label: 'Scrap' }
];

function partSearchHaystacks(part: NcrPart): string[] {
  return [
    part.part,
    part.description,
    part.product,
    part.type,
    part.class,
    ...(part.vendors || []).map(vendor => vendor.vendorcode || '')
  ].filter(Boolean);
}

const issueOptions = computed<SelectOption[]>(() => (
  issues.value.map(issue => ({ value: issue.id, label: `${issue.id}: ${issue.title}` }))
));
const modalTitle = computed(() => mode.value === 'create' ? 'New NCR' : recordId.value);
const isReadOnly = computed(() => mode.value === 'read-only');
const fileCount = computed(() => (
  Number(record.value?.fileSummary?.readyCount || 0)
  + (record.value?.files?.filter(file => !file.sourceFileId).length || 0)
));
const asanaUrl = computed(() => `https://app.asana.com/0/1202959342405780/${form.value.asana}/f`);

function modalService() {
  return (window as Window & {
    JidokaModal?: {
      show: (target: string, options?: { backdrop?: boolean | 'static'; keyboard?: boolean }) => void;
      hide: (target: string) => void;
    }
  }).JidokaModal;
}

function stopRecordSubscription(): void {
  unsubscribeRecord?.();
  unsubscribeRecord = null;
}

function subscribeRecord(id: string): void {
  stopRecordSubscription();
  unsubscribeRecord = ncrClient.subscribeToRecord(id, nextRecord => {
    record.value = nextRecord;
  });
}

async function show(): Promise<void> {
  await nextTick();
  modalService()?.show('#modal', { backdrop: 'static', keyboard: !isReadOnly.value });
}

function openCreate(): void {
  openGeneration += 1;
  stopRecordSubscription();
  mode.value = 'create';
  record.value = null;
  recordId.value = '';
  form.value = createEmptyNcrForm();
  stagedFiles.value = [];
  activeTab.value = 'messages';
  errorMessage.value = '';
  void show();
}

function renderEdit(nextRecord: NcrRecord): void {
  mode.value = 'edit';
  record.value = nextRecord;
  recordId.value = nextRecord.id;
  form.value = formFromRecord(nextRecord);
  stagedFiles.value = [];
  activeTab.value = 'messages';
  errorMessage.value = '';
  subscribeRecord(nextRecord.id);
  void show();
}

async function openEdit(nextRecord: NcrRecord | string): Promise<void> {
  const generation = ++openGeneration;
  if (typeof nextRecord === 'string') {
    const loadedRecord = await ncrClient.loadRecord(nextRecord);
    if (generation !== openGeneration) return;
    renderEdit(loadedRecord);
    return;
  }
  if (generation !== openGeneration) return;
  renderEdit(nextRecord);
}

async function openDeepLink(id: string): Promise<void> {
  const generation = ++openGeneration;
  errorMessage.value = '';
  try {
    const nextRecord = await ncrClient.loadRecord(id);
    if (generation !== openGeneration) return;
    renderEdit(nextRecord);
    mode.value = 'read-only';
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to load NCR';
    throw error;
  }
}

function updateRecord(nextRecord: NcrRecord): void {
  if (nextRecord.id === recordId.value) {
    record.value = nextRecord;
  }
}

function validate(): string | null {
  if (!form.value.newpart) return 'Part number is required.';
  const quantity = Number.parseInt(form.value.quantity, 10);
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 9999) {
    return 'Quantity must be a whole number from 1 to 9999.';
  }
  if (!form.value.severity) return 'Severity is required.';
  if (!form.value.description.trim()) return 'Description is required.';
  if (!form.value.stage) return 'Stage is required.';
  if (!form.value.timelost.trim()) return 'Time lost is required.';
  return null;
}

async function save(): Promise<void> {
  const validationError = validate();
  if (validationError) {
    errorMessage.value = validationError;
    return;
  }

  isSaving.value = true;
  errorMessage.value = '';
  try {
    let saved: NcrRecord;
    if (mode.value === 'create') {
      saved = await ncrClient.create(form.value, parts.value);
      mode.value = 'edit';
      record.value = saved;
      recordId.value = saved.id;
      subscribeRecord(saved.id);
    } else {
      saved = await ncrClient.update(recordId.value, form.value, parts.value);
      record.value = saved;
    }

    for (const stagedFile of [...stagedFiles.value]) {
      const [uploadedFile] = await uploadRecordFiles({
        recordType: 'ncr',
        recordId: saved.id,
        files: [stagedFile]
      });
      const downloadUrl = await fileClient.getDownloadUrl(uploadedFile.storagePath);
      await ncrClient.addModernFileMirror(saved.id, uploadedFile, downloadUrl);
      stagedFiles.value = stagedFiles.value.filter(file => file !== stagedFile);
    }

    recordId.value = saved.id;
    close();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to save NCR';
  } finally {
    isSaving.value = false;
  }
}

function stageFiles(event: Event): void {
  const selectedFiles = Array.from((event.target as HTMLInputElement).files || []);
  const validationError = selectedFiles.map(validateLocalRecordFile).find(Boolean);
  if (validationError) {
    errorMessage.value = validationError;
    (event.target as HTMLInputElement).value = '';
    return;
  }
  stagedFiles.value = selectedFiles;
}

async function removeLegacyFile(file: LegacyNcrFile): Promise<void> {
  if (!record.value || !window.confirm(`Delete ${file.name}?`)) return;
  try {
    const nextFiles = await ncrClient.removeLegacyFile(record.value.id, file, record.value.files || []);
    record.value = { ...record.value, files: nextFiles };
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to delete file';
  }
}

async function printLabel(): Promise<void> {
  if (!record.value || isPrinting.value) return;
  isPrinting.value = true;
  errorMessage.value = '';
  try {
    const jobId = await ncrClient.print(record.value, parts.value);
    window.alert(jobId ? `NCR label queued. Job ID: ${jobId}` : 'NCR label queued.');
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to print NCR label';
  } finally {
    isPrinting.value = false;
  }
}

function close(): void {
  modalService()?.hide('#modal');
}

function reset(): void {
  openGeneration += 1;
  stopRecordSubscription();
  record.value = null;
  recordId.value = '';
  form.value = createEmptyNcrForm();
  stagedFiles.value = [];
  activeTab.value = 'messages';
  errorMessage.value = '';
  isSaving.value = false;
  isPrinting.value = false;
}

const controller: NcrModalController = {
  create: openCreate,
  edit: openEdit,
  openDeepLink,
  updateRecord,
  reset
};

onMounted(() => {
  registerNcrModalController(controller);
  void ncrClient.loadParts()
    .then(nextParts => {
      parts.value = nextParts;
    })
    .catch(error => {
      console.warn('[NcrModal] Parts catalog failed to load:', error);
    })
    .finally(() => {
      partsLoading.value = false;
    });
  void ncrClient.loadIssues()
    .then(nextIssues => {
      issues.value = nextIssues;
    })
    .catch(error => {
      console.warn('[NcrModal] Issues failed to load:', error);
    });

  disposeHiddenListener = onDocumentModalEvent('hidden.bs.modal', (event: any) => {
    if (event?.target?.id === 'modal') reset();
  });
});

onBeforeUnmount(() => {
  stopRecordSubscription();
  disposeHiddenListener?.();
  if (window.NcrModalController === controller) delete window.NcrModalController;
});
</script>
